// 定时（循环）任务 API 模块
import { API_BASE_URL } from '../config'

// 统一响应格式
interface ApiResponse<T> {
  code: number
  message?: string
  data?: T
}

// 循环类型字符串 ↔ 后端 int
// 1-小时  2-天  3-月  4-季度  5-年
export type LoopTypeKey = 'hour' | 'day' | 'month' | 'quarter' | 'year'

export const LOOP_TYPE_MAP: Record<LoopTypeKey, number> = {
  hour: 1,
  day: 2,
  month: 3,
  quarter: 4,
  year: 5,
}

export const LOOP_TYPE_REVERSE: Record<number, LoopTypeKey> = {
  1: 'hour',
  2: 'day',
  3: 'month',
  4: 'quarter',
  5: 'year',
}

// 定时任务对象（后端字段结构）
export interface TaskLoop {
  id: number
  title: string
  level: number       // 1~4 优先级
  type: number        // 1=工作 2=日常
  loop_type: number   // 1~5
  loop_count: number  // 0=一直循环
  executed_count: number
  start_time: string  // HH:MM
  end_time: string    // HH:MM
  status: number      // 1 运行中 / 0 停止
  last_run_at: number
  next_run_at: number
  remark?: string
  create_at: number
  update_at: number
}

// 列表请求参数
export interface TaskLoopListParams {
  status?: number
  loop_type?: number
  type?: number
  level?: number
  keyword?: string
}

// 新增 / 编辑参数
export interface TaskLoopSaveParams {
  id?: number
  title: string
  level: number
  type: number
  loop_type: number
  loop_count: number
  start_time: string
  end_time: string
  status?: number
  remark?: string
}

// 通用请求封装
async function request<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  return response.json()
}

// 获取列表
export function getTaskLoopList(params: TaskLoopListParams = {}) {
  const query = new URLSearchParams()
  if (params.status !== undefined) query.append('status', String(params.status))
  if (params.loop_type !== undefined) query.append('loop_type', String(params.loop_type))
  if (params.type !== undefined) query.append('type', String(params.type))
  if (params.level !== undefined) query.append('level', String(params.level))
  if (params.keyword) query.append('keyword', params.keyword)
  const qs = query.toString()
  return request<TaskLoop[]>(`/api/task-loop/list${qs ? '?' + qs : ''}`)
}

// 详情
export function getTaskLoopDetail(id: number) {
  return request<TaskLoop>(`/api/task-loop/detail?id=${id}`)
}

// 新增
export function addTaskLoop(data: TaskLoopSaveParams) {
  return request<{ id: number }>('/api/task-loop/add', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// 编辑
export function editTaskLoop(data: TaskLoopSaveParams & { id: number }) {
  return request<null>('/api/task-loop/edit', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// 切换运行状态
export function toggleTaskLoop(id: number) {
  return request<{ status: number }>('/api/task-loop/toggle', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}

// 立即执行
export function runTaskLoop(id: number) {
  return request<{ task_id: number; executed_count: number; status: number }>(
    '/api/task-loop/run',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
  )
}

// 删除
export function deleteTaskLoop(id: number) {
  return request<null>('/api/task-loop/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  })
}
